import React, { useMemo, useState, useEffect, useRef, useReducer } from "react";
import { getLocale, useIntl } from "umi";
import moment from "moment";

import PerformanceMetricsTable from "@/pages/dashboard/components/PerformanceMetricsTable";

import {
  useTableColumns,
  useTableDataFormat,
  useTableState
} from "@/pages/dashboard/pages/overview/components/OverviewTable/hooks";
import useTableLoclesIntl from "@/pages/dashboard/pages/overview/_config/useTableLoclesIntl";
import "@/pages/dashboard/assets/scss/page/Overlay/components/tableConext.scss";
import {
  getVersionPerformanceTable
} from '@/pages/dashboard/services/overview';
import { formatFilterValues } from "@/pages/dashboard/utils/utils";
import { useOverLimitTrigger } from "@/pages/dashboard/components/OverLimitTip";
import { fomaterVersionData } from './util'
import versionData from "./versionData.json";
import './index.less'

export default function OverviewVersionTable({ filterValue, filterLoading, gameCode, explainInfo }) {
  // 表格 state 状态
  const {
    loading, setLoading,
    isError, setIsError,
    paging, setPaging,
    sort, setSort,
    total, setTotal,
    tableData, setTableData,
    statisticsData, setStatisticsData,
  } = useTableState();
  const { formatMessage } = useIntl()
  const [columns, setColumns] = useState([]);
  const { overLimitBarTriggerKey, handleResponse: handleOverLimitResponse } = useOverLimitTrigger();
  const [frontLogicalFlag, setFrontLogicalFlag] = useState<string | undefined>(undefined);
  const [expandedKeys, setExpandedKeys] = useState([]);

  const metircs: any = ['pay_amount', 'active_users', 'new_users', 'd2_t1', 'paid_users', 'arpu', 'arppu', 'payer_rate', 'total_users']
  const selectType = 'dod'
  const {
    DATE_COLUMN,
    getMetricColumns
  } = useTableColumns()
  const { COLUMN_CONFIG_VERSION } = useTableLoclesIntl()

  useEffect(() => {
    if (filterLoading) return;
    setPaging({ pageIndex: 1, pageSize: paging.pageSize });
    setSort([])
    const params = getRequestParams({ pageIndex: 1, pageSize: paging.pageSize }, [])
    getTableData(params);
  }, [filterValue, filterLoading])

  useEffect(() => {
    const dateColumn = filterValue.timeType === "Month" ? { ...DATE_COLUMN, width: 136 } : DATE_COLUMN
    const columns = [
      // dateColumn,
      {
        key: 'version',
        visible: false,
        header: formatMessage({ id: 'overview_Version' }),
        width: 160,
        fixed: "left",
        align: "left",
        render(record) {
          return record?.dimensions?.game_name
        }
      },
      ...getMetricColumns(metircs, COLUMN_CONFIG_VERSION, explainInfo, selectType)
    ]
    setColumns(columns);
  }, [filterValue, getLocale()])

  // 表格请求方法
  const getTableData = (params) => {
    setIsError(false);
    setLoading(true);
    getVersionPerformanceTable(params).then((resp) => {
      setLoading(false);
      const newData: any = []
      const newExpandedKeys: any = []
      resp?.data?.map(item => {
        if (item?.child) {
          item.child = fomaterVersionData(item.child, COLUMN_CONFIG_VERSION, filterValue.timeType)
          newExpandedKeys?.push(item?.dimensions?.game_code)
        } else {
          item.child = []
        }
        newData?.push(item)
      })
      setExpandedKeys(newExpandedKeys)
      setTableData(newData)
      // 设置总条数
      setTotal(newData?.length)
      setFrontLogicalFlag(resp?.front_logical_flag)
      handleOverLimitResponse(resp?.front_logical_flag)
    }).catch(e => {
      setLoading(false)
      setIsError(true);
      console.log("overview 宽表请求错误: ", filterValue.timeType, e);
    })
  }

  // 获取表格上传参数
  const getRequestParams = (paging, sort, otherParams?) => {
    // overview页面的日期上传参数逻辑与其他页面不同, 故特殊处理
    const { filterValue: filterVal, newFilter, diff } = filterValue;
    const { rangeTime, ...filters } = filterVal;
    let startTime = filterVal?.rangeTime?.[0];
    let endTime = filterVal?.rangeTime?.[1];
    const { by, order } = sort?.[0] || { by: 'date', order: 'desc' };
    /**
     * day 少于七天补足七天
     * week 少于六周补足六周
     * month 少于六月补足六月
     */
    if (newFilter && diff) {
      switch (filterValue.timeType) {
        case "Day": {
          startTime = moment(startTime).subtract(7, 'day').format('YYYY-MM-DD')
          break;
        }
        case "Week": {
          startTime = moment(endTime).subtract(5, 'week').format('YYYY-MM-DD')
          break;
        }
        case "Month": {
          startTime = moment(endTime).subtract(5, 'month').format('YYYY-MM-DD')
          break;
        }
      }
    }

    return {
      game_code: gameCode,
      start_date: moment(endTime).format('YYYY-MM-DD'),
      end_date: moment(endTime).format('YYYY-MM-DD'),
      filters: formatFilterValues(filters),
      accumulation: {
        by_version: true,
      },
      period: "daily",
      // 分页
      page: paging.pageIndex,
      page_size: paging.pageSize,
      sort_by: by?.replace(selectType, '') === 'version' || by?.replace(selectType, '') === 'date' ? 'game_code' : by?.replace(selectType, ''),
      desc: order === 'desc',
    }
  }
  // 表格数据
  const records = [
    ...fomaterVersionData(tableData, COLUMN_CONFIG_VERSION, filterValue.timeType),
  ]

  const onSortChange = (sorts) => {
    // 排序
    setSort(sorts)
    // 发起请求
    const params = getRequestParams(paging, sorts)
    getTableData(params);
  }

  return (
    <PerformanceMetricsTable
      className="dbd-version-table"
      title={useIntl().formatMessage({ id: "overview_overall_performance_version" }) + ` (${filterValue?.filterValue?.rangeTime?.[1]})`}
      // className='overall-conext'
      columns={columns}
      showDownloadBtn={false}
      selectType={selectType}
      recordKey={(record) => {
        return record?.dimensions?.game_code
      }}
      // copyData={copyData}
      records={records}
      timeType={filterValue?.timeType}
      sorting={{
        defaultSorts: sort,
        onSortChange: onSortChange,
      }}
      expand={{
        expandedKeys: expandedKeys,
        onExpandedKeysChange: (keys, { event }) => {
          setExpandedKeys(keys)
        }
      }}
      paging={{
        pageIndex: paging.pageIndex,
        pageSize: paging.pageSize,
        recordCount: total,
        pageSizeOptions: [10, 20, 30, 40, 50, 100, 200],
        onPagingChange(query) {
          // 切换分页
          setPaging(query);
          // 发起请求
          const params = getRequestParams(query, sort)
          getTableData(params);
        }
      }}
      onResizeEnd={(columns) => setColumns(columns)}
      loading={loading}
      isError={isError}
      frontLogicalFlag={frontLogicalFlag}
      overLimitBarTriggerKey={overLimitBarTriggerKey}
    />)
}
